import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  FaBold,
  FaItalic,
  FaStrikethrough,
  FaCode,
  FaHighlighter,
  FaParagraph,
  FaPaintBrush,
  FaQuoteLeft,
  FaListUl,
  FaListOl,
  FaMinus,
  FaLevelDownAlt,
  FaImage,
  FaLink,
} from "react-icons/fa";

import api from "@/services/api";
import { readFileAsync } from "@/utils";
import { FaBox, FaTableCells, FaTableCellsLarge } from "react-icons/fa6";

const FONT_SIZES = [
  { label: "Default", value: "" },
  { label: "Small", value: "12px" },
  { label: "Normal", value: "16px" },
  { label: "Large", value: "20px" },
  { label: "Extra Large", value: "24px" },
  { label: "2XL", value: "28px" },
  { label: "3XL", value: "32px" },
  { label: "4XL", value: "36px" },
  { label: "5XL", value: "40px" },
  { label: "6XL", value: "44px" },
  { label: "7XL", value: "48px" },
  { label: "8XL", value: "52px" },
  { label: "9XL", value: "56px" },
  { label: "10XL", value: "60px" },
];

export default ({ editor }) => {
  if (!editor) return null;

  const [showImageDropdown, setShowImageDropdown] = useState(false);

  const addLink = () => {
    const url = window.prompt("URL:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("Image URL:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const handleImageUpload = async (file) => {
    try {
      const rawBody = await readFileAsync(file);
      const { data } = await api.post("/file", { file: { rawBody, name: file.name }, folder: "/articles" });
      editor.chain().focus().setImage({ src: data }).run();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to upload image");
    }
  };

  return (
    <div className="p-2 flex flex-wrap gap-2 bg-gray-50 sticky top-0 z-50 border-b">
      <div className="flex flex-wrap gap-2 w-full pb-2">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("bold") ? "!bg-gray-300 border" : ""
          }`}>
          <FaBold title="Bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("italic") ? "!bg-gray-300 border" : ""
          }`}>
          <FaItalic title="Italic" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("strike") ? "!bg-gray-300 border" : ""
          }`}>
          <FaStrikethrough title="Strikethrough" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("code") ? "!bg-gray-300 border" : ""
          }`}>
          <FaCode title="Code" />
        </button>
        <div
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 flex items-center gap-1 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm cursor-pointer ${
            editor.isActive("strike") ? "!bg-gray-300 border" : ""
          }`}>
          <label>
            <input hidden type="color" onInput={(e) => editor.chain().focus().setColor(e.target.value).run()} className="text-gray-600 w-5 h-5" />
            <FaPaintBrush title="Color" />
          </label>
        </div>
        <div
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 flex items-center gap-1 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm cursor-pointer ${
            editor.isActive("strike") ? "!bg-gray-300 border" : ""
          }`}>
          <label>
            <input hidden type="color" onInput={(e) => editor.chain().focus().setHighlight({ color: e.target.value }).run()} className="text-gray-600 w-5 h-5" />
            <FaHighlighter title="Highlight" />
          </label>
        </div>
        <button
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("paragraph") ? "!bg-gray-300 border" : ""
          }`}>
          <FaParagraph title="Paragraph" />
        </button>
        {[1, 2, 3].map((level) => (
          <button
            key={level}
            onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
            className={`font-bold disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
              editor.isActive("heading", { level }) ? "!bg-gray-300 border" : ""
            }`}>
            H{level}
          </button>
        ))}
        <select
          className="border p-2 rounded-md text-sm"
          onChange={(event) => {
            if (event.target.value === "") {
              editor.chain().focus().unsetFontSize().run();
            } else {
              editor.chain().focus().setFontSize(event.target.value).run();
            }
          }}
          value={editor.getAttributes("textStyle").fontSize || ""}>
          {FONT_SIZES.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("blockquote") ? "!bg-gray-300 border" : ""
          }`}>
          <FaQuoteLeft title="Blockquote" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("bulletList") ? "!bg-gray-300 border" : ""
          }`}>
          <FaListUl title="Bullet List" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("orderedList") ? "!bg-gray-300 border" : ""
          }`}>
          <FaListOl title="Ordered List" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`font-bold disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("codeBlock") ? "!bg-gray-300 border" : ""
          }`}>
          {"{ }"}
        </button>
        <button
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm">
          <FaMinus title="Horizontal Rule" />
        </button>
        <button
          onClick={() => editor.chain().focus().setHardBreak().run()}
          className="disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm">
          <FaLevelDownAlt title="Hard Break" />
        </button>
        <button
          onClick={addLink}
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("link") ? "!bg-gray-300 border" : ""
          }`}>
          <FaLink title="Link" />
        </button>
        <button
          className={`disabled:cursor-not-allowed disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm ${
            editor.isActive("grid") ? "!bg-gray-300 border" : ""
          }`}
          onClick={() => editor.chain().focus().setGrid().run()}>
          <FaTableCellsLarge title="Grid" />
        </button>
        <div className="relative inline-block">
          <button
            onClick={() => setShowImageDropdown(!showImageDropdown)}
            className="disabled:cursor-not-allowed h-full disabled:bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm">
            <FaImage title="Image" />
          </button>
          {showImageDropdown && (
            <div className="absolute left-0 top-full mt-1 bg-white border rounded shadow-lg z-10 w-40">
              <label className="block px-4 py-2 hover:bg-gray-100 cursor-pointer text-xs">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                    setShowImageDropdown(false);
                  }}
                />
                Upload from computer
              </label>
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-xs"
                onClick={() => {
                  addImage();
                  setShowImageDropdown(false);
                }}>
                Import from URL
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
